import React, { useState, useEffect } from "react";
import {
  Row, Col, Alert, Container, Input, Label, Form, FormFeedback, Modal
} from "reactstrap";
//redux
import { useSelector, useDispatch } from "react-redux";

import { Link, useLocation, useNavigate } from "react-router-dom";

import PropTypes from "prop-types";

// withRouter
import withRouter from "src/components/Common/withRouter";

// actions
import { loginUser, socialLogin } from "../../slices/thunks";
import MfaStep from "./MfaStep";

import { useFormik } from "formik";
import * as Yup from "yup";
import { createSelector } from "reselect";
import { setAgency } from "src/slices/Auth/Agency/thunk";
import axiosInstance from "src/common/axiosInstance";
import {
  apiError,
  loginSuccess
} from "../../slices/Auth/Login/reducer";
interface LoginProps {
  props: any;
}
import useAgencyData from "../../common/useAgencyData";
import SelectAgencyModal from "./SelectAgencyModal";
const Login = ({ props }: LoginProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/analytics";
  const [agencies, setAgenies] = useState<any[]>([]);
  const [modalLarge, setModalLarge] = useState(false);
  const [loading, setLoading] = useState(false);
  // MFA state
  const [loginStep, setLoginStep] = useState<"credentials" | "mfa">("credentials");
  const [mfaToken, setMfaToken] = useState("");
  const { agencyName, agencyLogo, agencyId, domain, subdomainAgencyId } = useAgencyData();
  const [showAgencyMismatchModal, setShowAgencyMismatchModal] = useState(false);
  const [normalLoginUrl, setNormalLoginUrl] = useState("");

  const selectLeadData = createSelector(
    (state: any) => state.login.error,
    (leadsChart) => leadsChart
  );
  // Inside your component
  const error = useSelector(selectLeadData);
  function togLarge() {
    setModalLarge(!modalLarge);
    removeBodyCss();
    setLoading(!modalLarge)
  }
  function removeBodyCss() {
    document.body.classList.add("no_padding");
  }
  
  function setAgencyState(id: string | number) {
    if (id) {
      dispatch(setAgency({ agency_id: id }, navigate, true, from));
    } else {
      dispatch(setAgency({ agency_id: null }, navigate, true, from));
    }
  }

  /**
   * Shared post-login flow: fetch agencies and route the user appropriately.
   * Called after both a direct login (no MFA) and after a successful MFA verify.
   */
  async function handlePostLoginFlow(loginResult: any, email: string) {
    if (loginResult?.user || loginResult?.access_token) {
      const userEmail = loginResult?.user?.email || email;
      try {
        const response = await axiosInstance.get(`/agencies`, {
          params: { email: userEmail },
        });
        const res = await response.data;
        if (res && res.data) {
          setAgenies(res.data);
          if (subdomainAgencyId) {
            const isAssociated = res.data.some(
              (agency: any) => agency.agency_id === subdomainAgencyId
            );
            const getEnvUrl = () => {
              const env = process.env.REACT_APP_ENV;
              if (env === "local") return "https://testq1media.staging.unifieddatastudio.com/login";
              if (env === "staging") return "https://testq1media.staging.unifieddatastudio.com/login";
              if (env === "production") return "https://q1media.unifieddatastudio.com/login";
            };
            if (!isAssociated) {
              setLoading(false);
              setNormalLoginUrl(getEnvUrl() || "/login");
              setShowAgencyMismatchModal(true);
              return;
            }
            setAgencyState(subdomainAgencyId);
            setLoading(false);
          } else {
            if (res.data.length > 1) {
              togLarge();
            } else {
              const result = res.data;
              if (Object.keys(result).length) {
                const id = result[0].account_id;
                if (id) {
                  setAgencyState(id);
                  setLoading(false);
                }
              } else {
                setAgencyState('');
                setLoading(true);
              }
            }
          }
        }
      } catch {
        setLoading(false);
        dispatch(apiError("Failed to load agencies. Please try again."));
      }
    } else {
      setLoading(false);
      dispatch(apiError(loginResult?.message || "Login failed"));
    }
  }

  /** Called by MfaStep once verification succeeds */
  async function handleMfaSuccess(loginData: any) {
    setLoginStep("credentials");
    setMfaToken("");
    setLoading(true);
    await handlePostLoginFlow(loginData, loginData?.user?.email || "");
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async values => {
      setLoading(true);
      try {
        const loginResult = await dispatch(loginUser(values, navigate));

        // Thunk's catch block already dispatched the error — stop here
        if (!loginResult) {
          setLoading(false);
          return;
        }

        // Client user requires MFA — switch to the MFA step
        if (loginResult?.mfa_required) {
          setMfaToken(loginResult.mfa_token);
          setLoginStep("mfa");
          setLoading(false);
          return;
        }

        await handlePostLoginFlow(loginResult, values.email);
      } catch (error) {
        setLoading(false);
        dispatch(apiError("Invalid credentials"));
      }
    },
  });


  const signIn = (res: any, type: any) => {
    if (type === "google" && res) {
      const postData = {
        name: res.profileObj.name,
        email: res.profileObj.email,
        token: res.tokenObj.access_token,
        idToken: res.tokenId,
      };
      dispatch(socialLogin(postData, history, type));
    } else if (type === "facebook" && res) {
      const postData = {
        name: res.name,
        token: res.accessToken,
      };
      dispatch(socialLogin(postData, history, type));
    }
  };

  //handleGoogleLoginResponse
  const googleResponse = (response: Object) => {
    signIn(response, "google");
  };

  //handleFacebookLoginResponse
  const facebookResponse = (response: Object) => {
    signIn(response, "facebook");
  };

  return (
    <React.Fragment>
      <div className="authentication-bg min-vh-100">
        <div className="bg-overlay bg-white"></div>
        <Container>
          <div className="d-flex flex-column min-vh-100 px-3">
            <Row className="justify-content-center my-auto">
              <Col md={8} lg={6} xl={4}>
                <div className="text-center py-5">
                  <div className="mb-4 mb-md-5">
                    <Link to="/sales" className="d-block auth-logo">
                      <img
                        src={agencyLogo}
                        alt=""
                        height="22"
                        className="auth-logo-dark"
                        style={{ height: '60px' }}
                      />
                    </Link>
                  </div>
                  {loginStep === "mfa" ? (
                    /* ── MFA verification steps ── */
                    <MfaStep
                      mfaToken={mfaToken}
                      onSuccess={handleMfaSuccess}
                      onBack={() => {
                        setLoginStep("credentials");
                        setMfaToken("");
                        dispatch(apiError(""));
                      }}
                    />
                  ) : (
                    /* ── Email / password form ── */
                    <>
                      <div className="mb-4">
                        <h5>Welcome Back</h5>
                        <p>Sign in to continue to {agencyName} UDS.</p>
                      </div>
                      {error && error ? (<Alert className="alert-danger"> {error} </Alert>) : null}
                      <Form
                        onSubmit={e => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="form-floating form-floating-custom text-start mb-3">
                          <Input
                            type="text"
                            name="email"
                            className="form-control"
                            id="input-username"
                            placeholder="Enter Email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email && validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                          <Label htmlFor="input-username">Email</Label>
                          <div className="form-floating-icon">
                            <i className="uil uil-users-alt"></i>
                          </div>
                        </div>
                        <div className="form-floating form-floating-custom text-start mb-3">
                          <Input
                            name="password"
                            type="password"
                            className="form-control"
                            id="input-password"
                            placeholder="Enter Password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password || ""}
                            invalid={
                              validation.touched.password &&
                                validation.errors.password
                                ? true
                                : false
                            }
                          />
                          {validation.touched.password &&
                            validation.errors.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                          <Label htmlFor="input-password">Password</Label>
                          <div className="form-floating-icon">
                            <i className="uil uil-padlock"></i>
                          </div>
                        </div>
                        <div className="mt-3">
                          <button
                            className="btn btn-info w-100"
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? (
                              <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                              "Login"
                            )}
                          </button>
                        </div>

                        <div className="mt-4">
                          <Link
                            to="/forgot-password"
                            className="text-muted text-decoration-underline"
                          >
                            Forgot your password?
                          </Link>
                        </div>
                      </Form>
                    </>
                  )}
                </div>
              </Col>
            </Row>
            <Row className="my-4">
              <SelectAgencyModal
                isOpen={modalLarge}
                toggle={togLarge}
                agencies={agencies}
                onSelectAgency={setAgencyState}
                userId={0}
              />
            </Row>
          </div>
          <Modal isOpen={showAgencyMismatchModal} toggle={() => setShowAgencyMismatchModal(false)} centered>
            <div className="modal-header justify-content-center position-relative">
              <h5 className="modal-title w-100 text-center">
                Agency Access Denied
              </h5>
              <button
                type="button"
                className="btn-close position-absolute end-0 me-3"
                onClick={() => setShowAgencyMismatchModal(false)}
              ></button>
            </div>

            <div className="modal-body text-center">
              <p>
                You are currently not associated with this agency. Kindly log in using the URL provided below to continue.
              </p>
              <a href={normalLoginUrl} className="text-info fw-semibold">
                {normalLoginUrl}
              </a>
            </div>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);

Login.propTypes = {
  history: PropTypes.object,
};
